import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/Admin/Sidebar';
import AdminBottomNavBar from '../../components/Admin/AdminBottomNavBar';
import useAttendanceStore from '../../stores/attendanceStore';
import { getActualStudentSchedule } from '../../api';
import useAdminAuthStore from '../../stores/adminAuthStore';
import useStudentsStore from '../../stores/studentsStore';
import { formatDateToRussian } from '../../utils/dateUtils';

function AdminUserAttendance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminAuthStore();
  const { students, fetchStudents } = useStudentsStore();
  const student = useMemo(() => students.find(s => s._id === id), [students, id]);

  const { fetchStudentAttendance, markAttendance, studentAttendance } = useAttendanceStore();

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [rows, setRows] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [localStatus, setLocalStatus] = useState({});

  const monthRange = useMemo(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const start = new Date(y, m - 1, 1).toISOString().slice(0, 10);
    const end = new Date(y, m, 0).toISOString().slice(0, 10);
    return { start, end };
  }, [selectedMonth]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // Discover months dynamically from student's schedule in a 2-year window (current year and next year)
  useEffect(() => {
    async function discoverMonths() {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1).toISOString().slice(0,10);
      const end = new Date(now.getFullYear() + 1, 11, 31).toISOString().slice(0,10);
      const data = await getActualStudentSchedule({ studentId: id, startDate: start, endDate: end, token });
      const set = new Set();
      (data || []).forEach(d => {
        if (d.date) {
          // Корректируем дату для правильного определения месяца
          const originalDate = new Date(d.date);
          originalDate.setDate(originalDate.getDate() + 1);
          const ym = originalDate.toISOString().slice(0,7); // YYYY-MM
          set.add(ym);
        }
      });
      // Sort months desc (latest first)
      const list = Array.from(set).sort((a,b)=> b.localeCompare(a));
      setAvailableMonths(list);
      // If selected is not available, switch to latest available
      if (list.length && !list.includes(selectedMonth)) setSelectedMonth(list[0]);
    }
    discoverMonths();
  }, [id, token]);

  // Load schedule for student (all group + individual) for month
  useEffect(() => {
    async function load() {
      const data = await getActualStudentSchedule({ studentId: id, startDate: monthRange.start, endDate: monthRange.end, token });
      const list = [];
      (data || []).forEach(day => {
        const date = day.date;
        (day.timeSlots || []).forEach(slot => {
          list.push({
            date,
            startTime: slot.startTime,
            endTime: slot.endTime,
            subject: slot.subject,
            subjectName: slot.subject?.name || slot.subject,
            groupId: day.group || slot.groupId || null
          });
        });
      });
      list.sort((a,b)=> (a.date.localeCompare(b.date)) || (a.startTime||'').localeCompare(b.startTime||''));
      setRows(list);
    }
    load();
  }, [id, monthRange, token]);

  // Load student attendance statuses for month
  useEffect(() => {
    fetchStudentAttendance({ studentId: id, startDate: monthRange.start, endDate: monthRange.end, isAdmin: true });
  }, [id, monthRange, fetchStudentAttendance]);

  const statusByDateTime = useMemo(() => {
    const map = new Map();
    (studentAttendance || []).forEach(a => {
      const key = `${a.date}__${a.startTime}`;
      map.set(key, a.status || '');
    });
    return map;
  }, [studentAttendance]);

  const handleChange = async (row, status) => {
    const key = `${row.date}__${row.startTime}`;
    setLocalStatus(prev => ({ ...prev, [key]: status }));
    try {
      await markAttendance({ studentId: id, date: row.date, startTime: row.startTime, endTime: row.endTime, subjectId: row.subject?._id || row.subject, groupId: row.groupId, status });
      await fetchStudentAttendance({ studentId: id, startDate: monthRange.start, endDate: monthRange.end, isAdmin: true });
    } catch (e) {
      alert(e.message || 'Не удалось сохранить');
    }
  };

  const getValue = (row) => {
    const key = `${row.date}__${row.startTime}`;
    return localStatus[key] ?? statusByDateTime.get(key) ?? '';
  };

  return (
    <div className='AdminUserAttendance'>
      <div className="AdminUserAttendance__container">
        <Sidebar className="admin-sidebar" />
        <div className="content_admin" style={{ marginLeft: 240, paddingRight: 24 }}>
          <button className="admin-user-back-btn" onClick={() => navigate(-1)}>Назад</button>
          <h2 style={{ margin: '12px 0 18px' }}>Посещаемость ученика: {student?.fullName || ''}</h2>
          <div className="aua-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0 16px' }}>
            <span>Месяц:</span>
            <select className="aua-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
              {(availableMonths.length ? availableMonths : [selectedMonth]).map(ym => {
                const [y,m] = ym.split('-').map(Number);
                const d = new Date(y, m-1, 1);
                const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
                return <option key={ym} value={ym}>{label}</option>;
              })}
            </select>
          </div>
          <div className="aua-table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
          <table className='attendance-student-table aua-table' style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>Дата</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Время</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Предмет</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                // Добавляем +1 день для корректного отображения
                const originalDate = new Date(r.date);
                originalDate.setDate(originalDate.getDate() + 1);
                const displayDate = originalDate.toISOString().slice(0, 10);
                
                return (
                  <tr key={`${r.date}_${r.startTime}_${idx}`}>
                    <td style={{ padding: 8 }}>{formatDateToRussian(displayDate)}</td>
                    <td style={{ padding: 8 }}>{r.startTime}{r.endTime ? `–${r.endTime}` : ''}</td>
                    <td style={{ padding: 8 }}>{r.subjectName}</td>
                  <td style={{ padding: 8 }}>
                    <select value={getValue(r)} onChange={e => handleChange(r, e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #E0E0E6' }}>
                      <option value=''>—</option>
                      <option value='present'>Был</option>
                      <option value='absent'>Не был</option>
                    </select>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
          </div>
          <style>{`
            @media (max-width: 900px) {
              .AdminUserAttendance .content_admin { margin-left: 0 !important; padding: 0 12px; }
            }
            @media (max-width: 768px) {
              .aua-toolbar { flex-direction: column; align-items: stretch; gap: 8px; }
              .aua-select { width: 100%; padding: 8px; border-radius: 8px; }
              .aua-table { min-width: 520px; font-size: 13px; }
              .admin-user-back-btn { padding: 8px 12px; }
            }
          `}</style>
        </div>
      </div>
      <AdminBottomNavBar />
    </div>
  );
}

export default AdminUserAttendance;


